import {
  LitElement,
  html,
  css,
  classMap,
  styleMap,
  ref,
  createRef,
  map,
  range,
  unsafeHTML,
  styles,
  paths,
  utils,
  t,
} from '@hh/global.js'
const { throttle } = utils

class HHImageCarousel extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    speed: {
      reflect: true,
      type: Number,
      converter: {
        fromAttribute: (value) => {
          return Number(value * 1000)
        },
      },
    },
    playing: {
      reflect: true,
      type: Boolean,
    },
    userPause: {
      type: Boolean,
    },
    autoplay: {
      state: true,
    },
    toggle: {
      reflect: true,
      type: Boolean,
    },
    pagination: {
      reflect: true,
      type: Boolean,
    },
    loop: {
      reflect: true,
      type: Boolean,
    },
    counter: {
      reflect: true,
      type: Boolean,
    },
    arrows: {
      reflect: true,
      type: Boolean,
    },
    intersectionObserver: {
      reflect: true,
      type: Boolean,
      attribute: 'intersection-observer',
    },
    slideCount: {
      type: Number,
      attribute: 'slide-count',
    },
    hasDisruptor: {
      type: Boolean,
      attribute: 'has-disruptor',
    },
    hasAppendix: {
      type: Boolean,
      attribute: 'has-appendix',
    },
  }

  $slides = createRef()
  $next = createRef()
  $prev = createRef()
  $togglePlay = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this.playing = false
    this.userPause = false
    this.highlightCompleted = false

    this.touchstartX = 0
    this.touchendX = 0
    this.swipeThreshold = 50

    this.throttledUpdateUI = throttle(this.updateUI.bind(this), 100)

    this.mediaInFull = this.classList.contains('media-full') || false
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    if (this.mediaInFull) {
      setTimeout(() => {
        const headerHeight = document.querySelector('hh-header')?.getBoundingClientRect().height +
        document.querySelector('.js-announcement-bar-section')?.getBoundingClientRect().height +
        document.querySelector('.franchise-distribution')?.getBoundingClientRect().height

        this.style.setProperty('--hh-header-height', headerHeight + 'px' )
      }, 500);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.$slides?.value?.addEventListener('scroll', this.throttledUpdateUI)

    if (
      this.intersectionObserver &&
      this.speed &&
      !this.reducedMotion.matches
    ) {
      this.setPlayPauseIntersectionObserver()
    } else if (this.speed && !this.reducedMotion.matches) {
      this.setAutoplay()
    } else {
      this.autoplay = false
      this.userPause = true
    }

    this.updateUI()
  }

  updated(changedProperties) {
    if (changedProperties.has('_current')) {
      this.setSlideVisibility()
    }
  }

  updateUI() {
    if (!this._total) return

    this._current =
      Math.round(this.$slides?.value?.scrollLeft / this.items[0].clientWidth) +
      1

    if (!this.loop) {
      this._current === 1
        ? this.$prev?.value?.setAttribute('disabled', true)
        : this.$prev?.value?.removeAttribute('disabled')
      this._current === this._total
        ? this.$next?.value?.setAttribute('disabled', true)
        : this.$next?.value?.removeAttribute('disabled')
    }
  }

  setSlideVisibility() {
    this.items.forEach((item, idx) => {
      const linkElements = item.querySelectorAll('hh-button')
      if (linkElements.length === 0) return

      const idxOffset = idx + 1
      if (idxOffset === this._current) {
        linkElements.forEach((button) => {
          const linkElement = button.shadowRoot.querySelector('a')
          linkElement?.removeAttribute('tabindex')
        })
        item.setAttribute('aria-hidden', 'false')
        item.removeAttribute('tabindex')
      } else {
        linkElements.forEach((button) => {
          const linkElement = button.shadowRoot.querySelector('a')
          linkElement?.setAttribute('tabindex', '-1')
        })
        item.setAttribute('aria-hidden', 'true')
        item.setAttribute('tabindex', '-1')
      }
    })
  }

  // Autoplay methods
  setAutoplay() {
    if (!this.speed) return

    clearInterval(this.autoplay)
    this.autoplay = setInterval(this.rotateSlides.bind(this), this.speed)
    this.play()
  }

  stopAutoplay() {
    clearInterval(this.autoplay)
    this.pause()
  }

  toggleAutoplay() {
    if (this.playing) {
      this.userPause = true
      this.stopAutoplay()
    } else {
      this.userPause = false
      this.setAutoplay()
    }
  }

  play() {
    this.container?.setAttribute('aria-live', 'off')
    this.playing = true
  }

  pause() {
    this.container?.setAttribute('aria-live', 'polite')
    this.playing = false
  }

  rotateSlides() {
    const slideScrollPosition =
      this._current === this._total
        ? 0
        : this.$slides?.value?.scrollLeft + this.items[0].clientWidth
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })

    if (this._current !== this._total) {
      const nextSlideImage = this.items[this._current].querySelector('img')
      this.eagerLoadImage(nextSlideImage)
    }
  }

  setPlayPauseIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) =>
        entry.isIntersecting ? this.onFocusOut() : this.onFocusIn()
      )
    })
    observer.observe(this.$slides?.value)
  }

  // Early load image for next slide
  eagerLoadImage(image) {
    if (!image) return
    image.setAttribute('loading', 'eager')
  }

  // Move to next or previous slide
  move(e) {
    e.preventDefault()
    this.userPause = true
    this.stopAutoplay()
    let slideScrollPosition

    if (e.currentTarget.name === 'next') {
      if (this.loop && this._current === this._total) {
        slideScrollPosition = 0
      } else {
        slideScrollPosition =
          this.$slides?.value?.scrollLeft + this.items[0].clientWidth
      }
    } else {
      if (this.loop && this._current === 1) {
        slideScrollPosition =
          this.items[this._total - 1].offsetLeft + this.items[0].clientWidth
      } else {
        slideScrollPosition =
          this.$slides?.value?.scrollLeft - this.items[0].clientWidth
      }
    }

    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  // Move to specific slide
  moveTo(slide) {
    const slideScrollPosition = this.items[slide].offsetLeft
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })

    const nextSlideImage = this.items[slide].querySelector('img')
    this.eagerLoadImage(nextSlideImage)
  }

  // Event handlers
  onFocusIn() {
    if (this.userPause || this.reducedMotion.matches) return
    this.stopAutoplay()
  }

  onFocusOut() {
    if (this.userPause || this.reducedMotion.matches) return
    this.setAutoplay()
  }

  onPaginationClick(e, slideIndex) {
    e.preventDefault()
    this.userPause = true
    this.stopAutoplay()
    this.moveTo(slideIndex)
  }

  onTouchStart(e) {
    this.touchstartX = e.changedTouches[0].screenX
  }

  onTouchEnd(e) {
    this.touchendX = e.changedTouches[0].screenX
    this.checkSwipe()
  }

  checkSwipe() {
    if (
      this.touchendX < this.touchstartX - this.swipeThreshold ||
      this.touchendX > this.touchstartX + this.swipeThreshold
    ) {
      this.userPause = true
      this.stopAutoplay()
    }
  }

  controlsBottomPosition() {
    return this.hasDisruptor && this.hasAppendix
      ? '9rem'
      : this.hasDisruptor
      ? '5rem'
      : this.hasAppendix
      ? '4.5rem'
      : '0'
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      <div
        class="wrapper"
        @touchstart="${this.onTouchStart}"
        @touchend="${this.onTouchEnd}"
        @mouseenter="${this.onFocusIn}"
        @mouseleave="${this.onFocusOut}"
        @focusin="${this.onFocusIn}"
        @focusout="${this.onFocusOut}"
      >
        <div class="slides" ${ref(this.$slides)}>
          <slot name="item"></slot>
        </div>
        ${/* Previous/next buttons */ ''}
        ${(this.arrows &&
          html`
            <div
              class=${classMap({
                arrows: true,
                hidden: this.slideCount === 1,
              })}
            >
              <button
                ${ref(this.$prev)}
                @click="${this.move}"
                class="arrow arrow--prev"
                name="previous"
                aria-label="${t('accessibility.previous_slide')}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
              <button
                ${ref(this.$next)}
                @click="${this.move}"
                class="arrow arrow--next"
                name="next"
                aria-label="${t('accessibility.next_slide')}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            </div>
          `) ||
        ''}
        <div
          class="controls"
          style=${styleMap({
            bottom: this.controlsBottomPosition(),
          })}
        >
          ${/* Pagination */ ''}
          ${(this.pagination &&
            html`
              <div
                class="${classMap({
                  pagination: true,
                  hidden: this.slideCount === 1,
                })}"
              >
                ${map(range(this.slideCount), (idx) => {
                  return html`
                    <button
                      class=${classMap({
                        'pagination-button': true,
                        active: this._current === idx + 1,
                      })}
                      aria-label="${t(
                        'sections.image_carousel.go_to_slide'
                      )} ${idx + 1}"
                      aria-current="${this._current === idx + 1}"
                      @click="${(e) => this.onPaginationClick(e, idx)}"
                    >
                      <span class="pagination-button-inner"></span>
                    </button>
                  `
                })}
              </div>
            `) ||
          ''}
          ${/* Play/pause button */ ''}
          ${(this.toggle &&
            html`
              <button
                class="${classMap({
                  toggle: true,
                  hidden: !this.speed || this.slideCount === 1,
                })}"
                @click=${this.toggleAutoplay}
                aria-label=${this.playing
                  ? t('sections.image_carousel.pause')
                  : t('sections.image_carousel.play')}
              >
                <span
                  class="${classMap({
                    hidden: this.playing,
                  })}"
                  >${unsafeHTML(paths.play)}</span
                >
                <span
                  class="${classMap({
                    hidden: !this.playing,
                  })}"
                  >${unsafeHTML(paths.pause)}</span
                >
              </button>
            `) ||
          ''}
          ${/* Counter */ ''}
          ${(this.counter &&
            html`
              <div class="counter visually-hidden">
                <span class="counter--current">${this._current}</span>
                <span aria-hidden="true"> / </span>
                <span class="visually-hidden">${t('accessibility.of')}</span>
                <span class="counter--total"
                  >${this._total ? this._total : ''}</span
                >
              </div>
            `) ||
          ''}
        </div>
      </div> `
  }

  static styles = [
    styles.visuallyHidden,
    styles.hidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      .wrapper {
        position: relative;
      }

      .slides {
        display: flex;
        flex-wrap: nowrap;
        align-items: var(--hh-align-items, center);
        position: relative;
        width: 100%;
        background-color: var(--hh-carousel-background, transparent);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
      }

      .slides::-webkit-scrollbar {
        display: none;
      }

      .controls {
        position: absolute;
        padding: 1.6rem 3rem;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        column-gap: 1rem;
      }

      @media (min-width: 1024) {
        .controls {
          padding-left: 4rem;
        }
      }

      @media (min-width: 1400px) {
        .controls {
          padding-left: 9.6rem;
        }
      }

      .pagination {
        display: flex;
      }

      .pagination-button {
        padding: 0.8rem 0.4rem;
        background-color: transparent;
        border: none;
        cursor: pointer;
      }

      .pagination-button:focus-visible {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      .pagination-button-inner {
        display: block;
        width: 0.5rem;
        height: 0.5rem;
        background-color: var(--hh-color-background);
        opacity: 0.5;
        border-radius: 5rem;
      }

      .pagination-button.active .pagination-button-inner {
        width: 6rem;
        opacity: 1;
      }

      .toggle {
        width: var(--hh-toggle-button-size, 2.4rem);
        height: var(--hh-toggle-button-size, 2.4rem);
        padding: 0;
        color: var(--hh-color-background);
        background-color: transparent;
        border: none;
        cursor: pointer;
        flex-shrink: 0;
        opacity: 0;
        pointer-events: none;
      }

      .toggle:focus-visible {
        opacity: 1;
        pointer-events: auto;
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      .toggle svg {
        width: 100%;
        height: 100%;
      }

      .counter {
        display: flex;
        column-gap: 0.5rem;
        color: var(--hh-color-background);
      }

      .arrows {
        display: none;
        column-gap: 0.5rem;
      }

      @media (min-width: 1024px) {
        .arrows {
          display: flex;
        }
      }

      .arrow {
        width: var(--hh-arrow-button-size, 3.2rem);
        height: var(--hh-arrow-button-size, 3.2rem);
        padding: 0;
        color: var(--hh-color-background);
        background-color: transparent;
        border: none;
        cursor: pointer;
      }

      .arrow:focus-visible {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      .arrow[disabled] {
        opacity: 0.5;
        cursor: default;
      }

      .arrow--prev {
        position: absolute;
        left: 2rem;
        top: 50%;
        transform: translateY(-50%) rotate(90deg);
      }

      .arrow--next {
        position: absolute;
        right: 2rem;
        top: 50%;
        transform: translateY(-50%) rotate(-90deg);
      }
    `,
  ]
}

customElements.define('hh-image-carousel', HHImageCarousel)
